/**
 * Wizard de importación CSV/XLSX. Tres pasos:
 *  1. Seleccionar archivo + (opcional) plantilla guardada.
 *  2. Mapear columnas → campos de transacción + ver preview.
 *  3. Defaults globales (cuenta, affects_totals, etc.) + ejecutar import.
 *
 * Parseo del archivo en el cliente (xlsx lazy import). Solo el JSON final
 * de filas se manda al server, así no cargamos la lib en el bundle inicial
 * y el Worker no procesa archivos pesados.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, ArrowLeft, ArrowRight, Save, Trash2, FileSpreadsheet } from "lucide-react";
import clsx from "clsx";
import type { Account, ImportMapping } from "@axorial/shared";
import { api } from "@/lib/api";
import { formatMxnCents } from "@/lib/format";
import { BulkImportPanel } from "./import/BulkImportPanel";

interface ImportTemplate {
  id: string;
  name: string;
  accountId: string | null;
  mapping: ImportMapping;
  notes: string | null;
}

interface Category {
  id: string;
  name: string;
  kind: "ingreso" | "gasto";
}

type Row = Record<string, string>;

interface ParsedRow {
  date: string; // YYYY-MM-DD
  description: string;
  kind: "ingreso" | "gasto";
  amountCents: number;
  raw: Row;
  error?: string;
}

const DATE_FORMATS = [
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY (12/05/2026)" },
  { value: "MM/DD/YYYY", label: "MM/DD/YYYY (US)" },
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD (ISO)" },
  { value: "DD-MM-YYYY", label: "DD-MM-YYYY" },
];

export function ImportTab() {
  const [mode, setMode] = useState<"bulk" | "custom">("bulk");
  return (
    <div className="space-y-4">
      <div role="tablist" className="tabs tabs-boxed w-fit">
        <button
          type="button"
          role="tab"
          onClick={() => setMode("bulk")}
          className={`tab ${mode === "bulk" ? "tab-active" : ""}`}
        >
          Bulk (BBVA / Inbursa / Konfio)
        </button>
        <button
          type="button"
          role="tab"
          onClick={() => setMode("custom")}
          className={`tab ${mode === "custom" ? "tab-active" : ""}`}
        >
          CSV / XLSX custom
        </button>
      </div>
      {mode === "bulk" ? <BulkImportPanel /> : <CustomImportWizard />}
    </div>
  );
}

function CustomImportWizard() {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [file, setFile] = useState<File | null>(null);
  const [rawRows, setRawRows] = useState<Row[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);

  const [mapping, setMapping] = useState<ImportMapping>({
    date: "",
    description: "",
    amount: "",
    dateFormat: "DD/MM/YYYY",
  });
  const [amountMode, setAmountMode] = useState<"single" | "two_columns">("single");

  const [accountId, setAccountId] = useState("");
  const [affectsTotals, setAffectsTotals] = useState(false);
  const [isrApplicable, setIsrApplicable] = useState(true);
  const [defaultIngresoCat, setDefaultIngresoCat] = useState("");
  const [defaultGastoCat, setDefaultGastoCat] = useState("");
  const [defaultInvoiceStatus, setDefaultInvoiceStatus] = useState<
    "facturado" | "deducible_sin_factura" | "pendiente" | "no_aplica"
  >("pendiente");

  const accounts = useQuery<{ accounts: Account[] }>({
    queryKey: ["finance", "accounts"],
    queryFn: () => api.get("/finance/accounts"),
  });
  const categories = useQuery<{ categories: Category[] }>({
    queryKey: ["finance", "categories"],
    queryFn: () => api.get("/finance/categories"),
  });
  const templates = useQuery<{ templates: ImportTemplate[] }>({
    queryKey: ["finance", "import", "templates"],
    queryFn: () => api.get("/finance/import/templates"),
  });

  async function handleFile(f: File) {
    setFile(f);
    setParseError(null);
    setParsing(true);
    try {
      // Migrado de `xlsx` (SheetJS) a `exceljs` por vuln HIGH sin fix
      // upstream (GHSA-4r6h-8v6p-xvw6 + GHSA-5pgg-2g8v-p4x9). exceljs ya
      // estaba en deps (lo usa exportExcel.ts) — un solo parser para
      // toda la app.
      const ExcelJSMod = await import("exceljs");
      const ExcelJS = (ExcelJSMod as { default?: typeof import("exceljs") }).default ?? ExcelJSMod;
      const wb = new ExcelJS.Workbook();
      const buf = await f.arrayBuffer();
      await wb.xlsx.load(buf);
      const sheet = wb.worksheets[0];
      if (!sheet) throw new Error("Archivo vacío.");
      // Convertir a JSON con la fila 1 como headers — mismo shape que
      // `sheet_to_json` devolvía para que el resto del flujo no cambie.
      const headers: string[] = [];
      sheet.getRow(1).eachCell({ includeEmpty: true }, (cell, col) => {
        headers[col - 1] = String(cell.value ?? "").trim();
      });
      const json: Row[] = [];
      sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber === 1) return;
        const r: Row = {};
        row.eachCell({ includeEmpty: true }, (cell, col) => {
          const key = headers[col - 1];
          if (!key) return;
          // Convertimos a string preservando representación; el caller ya
          // parsea con cleanNumber/parseSpanishDate según corresponde.
          const v = cell.value;
          if (v == null) r[key] = "";
          else if (v instanceof Date) r[key] = v.toISOString().slice(0, 10);
          else if (typeof v === "object" && "result" in v)
            r[key] = String((v as { result?: unknown }).result ?? "");
          else r[key] = String(v);
        });
        json.push(r);
      });
      if (json.length === 0) throw new Error("Sin filas en el archivo.");
      const cols = headers.filter(Boolean);
      setRawRows(json);
      setColumns(cols);
      setStep(2);
    } catch (e) {
      setParseError(e instanceof Error ? e.message : String(e));
    } finally {
      setParsing(false);
    }
  }

  // Auto-detectar columnas comunes al subir.
  useEffect(() => {
    if (columns.length === 0) return;
    const guess = (patterns: RegExp[]) =>
      columns.find((c) => patterns.some((p) => p.test(c.toLowerCase()))) ?? "";
    setMapping((prev) => ({
      date: prev.date || guess([/fecha/, /date/, /operaci/]),
      description: prev.description || guess([/concepto/, /descrip/, /detalle/, /referencia/]),
      amount: prev.amount || guess([/^monto$/, /^importe$/, /^amount$/, /^valor$/]),
      cargoCol: prev.cargoCol || guess([/cargo|debe|d[eé]bito/]),
      abonoCol: prev.abonoCol || guess([/abono|haber|cr[eé]dito/]),
      dateFormat: prev.dateFormat ?? "DD/MM/YYYY",
    }));
    if (
      columns.some((c) => /cargo|debe|d[eé]bito/.test(c.toLowerCase())) ||
      columns.some((c) => /abono|haber|cr[eé]dito/.test(c.toLowerCase()))
    ) {
      setAmountMode("two_columns");
    }
  }, [columns]);

  function applyTemplate(t: ImportTemplate) {
    setMapping(t.mapping);
    if (t.accountId) setAccountId(t.accountId);
    setAmountMode(t.mapping.cargoCol || t.mapping.abonoCol ? "two_columns" : "single");
  }

  const parsedRows = useMemo(
    () => parseRows(rawRows, mapping, amountMode),
    [rawRows, mapping, amountMode],
  );

  return (
    <div className="space-y-4">
      <Stepper current={step} />

      {step === 1 && (
        <Step1
          file={file}
          onFile={handleFile}
          parsing={parsing}
          error={parseError}
          templates={templates.data?.templates ?? []}
          onApplyTemplate={(t) => {
            applyTemplate(t);
          }}
        />
      )}

      {step === 2 && (
        <Step2
          columns={columns}
          mapping={mapping}
          onMappingChange={setMapping}
          amountMode={amountMode}
          onAmountModeChange={setAmountMode}
          previewRows={parsedRows.slice(0, 8)}
          totalRows={parsedRows.length}
          errorCount={parsedRows.filter((r) => r.error).length}
          accounts={accounts.data?.accounts ?? []}
          accountId={accountId}
          onAccountChange={setAccountId}
          onBack={() => setStep(1)}
          onNext={() => setStep(3)}
          mappingValid={
            !!mapping.date &&
            !!mapping.description &&
            (amountMode === "single" ? !!mapping.amount : !!mapping.cargoCol || !!mapping.abonoCol)
          }
        />
      )}

      {step === 3 && (
        <Step3
          parsedRows={parsedRows}
          accountId={accountId}
          accounts={accounts.data?.accounts ?? []}
          categories={categories.data?.categories ?? []}
          mapping={mapping}
          affectsTotals={affectsTotals}
          isrApplicable={isrApplicable}
          defaultIngresoCat={defaultIngresoCat}
          defaultGastoCat={defaultGastoCat}
          defaultInvoiceStatus={defaultInvoiceStatus}
          onAffectsTotalsChange={setAffectsTotals}
          onIsrApplicableChange={setIsrApplicable}
          onDefaultIngresoCatChange={setDefaultIngresoCat}
          onDefaultGastoCatChange={setDefaultGastoCat}
          onDefaultInvoiceStatusChange={setDefaultInvoiceStatus}
          onBack={() => setStep(2)}
          onDone={() => {
            setStep(1);
            setFile(null);
            setRawRows([]);
            setColumns([]);
          }}
        />
      )}
    </div>
  );
}

function Stepper({ current }: { current: 1 | 2 | 3 }) {
  const steps = [
    { n: 1, label: "Archivo" },
    { n: 2, label: "Mapeo" },
    { n: 3, label: "Defaults + import" },
  ] as const;
  return (
    <ul className="steps w-full">
      {steps.map((s) => (
        <li key={s.n} className={clsx("step", s.n <= current && "step-primary")} data-content={s.n}>
          {s.label}
        </li>
      ))}
    </ul>
  );
}

function Step1({
  file,
  onFile,
  parsing,
  error,
  templates,
  onApplyTemplate,
}: {
  file: File | null;
  onFile: (f: File) => void;
  parsing: boolean;
  error: string | null;
  templates: ImportTemplate[];
  onApplyTemplate: (t: ImportTemplate) => void;
}) {
  return (
    <div className="card-elevated p-6 space-y-4">
      <div>
        <h3 className="text-base font-display font-semibold mb-1">1. Selecciona el archivo</h3>
        <p className="text-sm text-base-content/65">
          CSV o XLSX (estado de cuenta, planilla histórica, etc.). Se procesa en tu navegador — nada
          se sube hasta el último paso.
        </p>
      </div>

      <label className="cursor-pointer flex items-center justify-center gap-3 px-4 py-8 rounded-md border-2 border-dashed border-base-300/80 hover:border-primary/50 hover:bg-base-200/40 text-base-content/65">
        <Upload className="size-5" />
        <span className="text-sm">
          {file ? <strong>{file.name}</strong> : "Selecciona un archivo (.csv o .xlsx)"}
        </span>
        <input
          type="file"
          className="hidden"
          accept=".csv,.xlsx,.xls"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
          }}
        />
      </label>

      {parsing && (
        <div className="flex items-center gap-2 text-sm">
          <span className="loading loading-spinner loading-xs text-primary" /> Procesando…
        </div>
      )}
      {error && <div className="alert alert-error text-sm py-2">{error}</div>}

      {templates.length > 0 && (
        <div className="border-t border-base-300/60 pt-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-base-content/55 mb-2">
            Plantillas guardadas
          </div>
          <ul className="space-y-1">
            {templates.map((t) => (
              <li key={t.id} className="flex items-center gap-3 text-sm">
                <FileSpreadsheet className="size-4 text-base-content/45 shrink-0" />
                <span className="flex-1 truncate">{t.name}</span>
                <button
                  type="button"
                  onClick={() => onApplyTemplate(t)}
                  className="btn btn-ghost btn-xs"
                >
                  Aplicar mapping
                </button>
                <DeleteTemplateButton id={t.id} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function DeleteTemplateButton({ id }: { id: string }) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api.del(`/finance/import/templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["finance", "import", "templates"] }),
  });
  return (
    <button
      type="button"
      onClick={() => {
        if (confirm("¿Borrar plantilla?")) remove.mutate();
      }}
      disabled={remove.isPending}
      className="btn btn-ghost btn-xs btn-square text-error"
      aria-label="Borrar plantilla"
    >
      <Trash2 className="size-3.5" />
    </button>
  );
}

function Step2({
  columns,
  mapping,
  onMappingChange,
  amountMode,
  onAmountModeChange,
  previewRows,
  totalRows,
  errorCount,
  accounts,
  accountId,
  onAccountChange,
  onBack,
  onNext,
  mappingValid,
}: {
  columns: string[];
  mapping: ImportMapping;
  onMappingChange: (m: ImportMapping) => void;
  amountMode: "single" | "two_columns";
  onAmountModeChange: (m: "single" | "two_columns") => void;
  previewRows: ParsedRow[];
  totalRows: number;
  errorCount: number;
  accounts: Account[];
  accountId: string;
  onAccountChange: (id: string) => void;
  onBack: () => void;
  onNext: () => void;
  mappingValid: boolean;
}) {
  const set = <K extends keyof ImportMapping>(k: K, v: ImportMapping[K]) =>
    onMappingChange({ ...mapping, [k]: v });

  return (
    <div className="card-elevated p-6 space-y-4">
      <div>
        <h3 className="text-base font-display font-semibold mb-1">2. Mapea las columnas</h3>
        <p className="text-sm text-base-content/65">
          Indica qué columna del archivo corresponde a cada campo. Detectamos automáticamente las
          que tengan nombres comunes.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ColMap
          label="Fecha"
          value={mapping.date}
          columns={columns}
          onChange={(v) => set("date", v)}
        />
        <ColMap
          label="Descripción / concepto"
          value={mapping.description}
          columns={columns}
          onChange={(v) => set("description", v)}
        />
        <label className="form-control block">
          <div className="label py-1">
            <span className="label-text text-sm font-medium">Formato de fecha</span>
          </div>
          <select
            className="select select-bordered select-sm w-full"
            value={mapping.dateFormat ?? "DD/MM/YYYY"}
            onChange={(e) => set("dateFormat", e.target.value)}
          >
            {DATE_FORMATS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="space-y-2">
        <div className="text-sm font-medium">¿Cómo viene el monto?</div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onAmountModeChange("single")}
            className={clsx(
              "btn btn-sm",
              amountMode === "single" ? "btn-primary" : "btn-ghost border border-base-300/60",
            )}
          >
            Una columna (positivo/negativo)
          </button>
          <button
            type="button"
            onClick={() => onAmountModeChange("two_columns")}
            className={clsx(
              "btn btn-sm",
              amountMode === "two_columns" ? "btn-primary" : "btn-ghost border border-base-300/60",
            )}
          >
            Dos columnas (cargo / abono)
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {amountMode === "single" ? (
            <ColMap
              label="Monto"
              value={mapping.amount ?? ""}
              columns={columns}
              onChange={(v) => set("amount", v)}
            />
          ) : (
            <>
              <ColMap
                label="Cargo (gasto)"
                value={mapping.cargoCol ?? ""}
                columns={columns}
                onChange={(v) => set("cargoCol", v)}
              />
              <ColMap
                label="Abono (ingreso)"
                value={mapping.abonoCol ?? ""}
                columns={columns}
                onChange={(v) => set("abonoCol", v)}
              />
            </>
          )}
        </div>
      </div>

      <div>
        <div className="text-sm font-medium mb-1">Cuenta destino</div>
        <select
          className="select select-bordered select-sm w-full max-w-md"
          value={accountId}
          onChange={(e) => onAccountChange(e.target.value)}
        >
          <option value="">— Selecciona —</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>

      <PreviewTable rows={previewRows} totalRows={totalRows} errorCount={errorCount} />

      <SaveTemplateRow mapping={mapping} accountId={accountId} disabled={!mappingValid} />

      <div className="flex justify-between pt-3 border-t border-base-300/60">
        <button type="button" onClick={onBack} className="btn btn-ghost btn-sm">
          <ArrowLeft className="size-4" /> Atrás
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!mappingValid || !accountId}
          className="btn btn-primary btn-sm"
        >
          Siguiente <ArrowRight className="size-4" />
        </button>
      </div>
    </div>
  );
}

function ColMap({
  label,
  value,
  columns,
  onChange,
}: {
  label: string;
  value: string;
  columns: string[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="form-control block">
      <div className="label py-1">
        <span className="label-text text-sm font-medium">{label}</span>
      </div>
      <select
        className="select select-bordered select-sm w-full"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— Selecciona columna —</option>
        {columns.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    </label>
  );
}

function PreviewTable({
  rows,
  totalRows,
  errorCount,
}: {
  rows: ParsedRow[];
  totalRows: number;
  errorCount: number;
}) {
  return (
    <div className="space-y-2">
      <div className="text-xs text-base-content/65">
        Preview · {totalRows} filas detectadas
        {errorCount > 0 && (
          <span className="text-error ml-2">
            · {errorCount} con error (se omitirán al importar)
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="table table-xs">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Descripción</th>
              <th>Tipo</th>
              <th className="text-right">Monto</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-base-content/50 italic py-3">
                  Configura el mapping para ver preview
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={i} className={r.error ? "text-error" : ""}>
                  <td className="font-mono">{r.date || "—"}</td>
                  <td className="truncate max-w-xs">{r.description}</td>
                  <td>
                    <span
                      className={clsx(
                        "badge badge-xs",
                        r.kind === "ingreso" ? "badge-success" : "badge-error",
                      )}
                    >
                      {r.kind}
                    </span>
                  </td>
                  <td className="text-right font-mono tabular-nums">
                    {formatMxnCents(r.amountCents)}
                  </td>
                  <td className="text-xs">{r.error ?? "OK"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SaveTemplateRow({
  mapping,
  accountId,
  disabled,
}: {
  mapping: ImportMapping;
  accountId: string;
  disabled: boolean;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api.post("/finance/import/templates", {
        name,
        accountId: accountId || undefined,
        mapping,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance", "import", "templates"] });
      setName("");
      setOpen(false);
    },
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="btn btn-ghost btn-xs gap-1 text-base-content/65"
      >
        <Save className="size-3.5" /> Guardar este mapping como plantilla
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        className="input input-bordered input-sm flex-1 max-w-xs"
        placeholder="Nombre (ej. BBVA Empresarial)"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <button
        type="button"
        onClick={() => save.mutate()}
        disabled={!name.trim() || save.isPending}
        className="btn btn-primary btn-xs"
      >
        Guardar
      </button>
      <button type="button" onClick={() => setOpen(false)} className="btn btn-ghost btn-xs">
        Cancelar
      </button>
    </div>
  );
}

function Step3({
  parsedRows,
  accountId,
  accounts,
  categories,
  mapping: _mapping,
  affectsTotals,
  isrApplicable,
  defaultIngresoCat,
  defaultGastoCat,
  defaultInvoiceStatus,
  onAffectsTotalsChange,
  onIsrApplicableChange,
  onDefaultIngresoCatChange,
  onDefaultGastoCatChange,
  onDefaultInvoiceStatusChange,
  onBack,
  onDone,
}: {
  parsedRows: ParsedRow[];
  accountId: string;
  accounts: Account[];
  categories: Category[];
  mapping: ImportMapping;
  affectsTotals: boolean;
  isrApplicable: boolean;
  defaultIngresoCat: string;
  defaultGastoCat: string;
  defaultInvoiceStatus: "facturado" | "deducible_sin_factura" | "pendiente" | "no_aplica";
  onAffectsTotalsChange: (b: boolean) => void;
  onIsrApplicableChange: (b: boolean) => void;
  onDefaultIngresoCatChange: (id: string) => void;
  onDefaultGastoCatChange: (id: string) => void;
  onDefaultInvoiceStatusChange: (
    s: "facturado" | "deducible_sin_factura" | "pendiente" | "no_aplica",
  ) => void;
  onBack: () => void;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const validRows = parsedRows.filter((r) => !r.error);
  const account = accounts.find((a) => a.id === accountId);

  const run = useMutation<{ ok: true; inserted: number }>({
    mutationFn: () =>
      api.post("/finance/import/run", {
        accountId,
        affectsTotals,
        isrApplicable,
        defaultCategoryIngresoId: defaultIngresoCat || undefined,
        defaultCategoryGastoId: defaultGastoCat || undefined,
        defaultInvoiceStatus,
        rows: validRows.map((r) => ({
          date: r.date,
          description: r.description,
          kind: r.kind,
          amountCents: r.amountCents,
        })),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance"] });
    },
  });

  return (
    <div className="card-elevated p-6 space-y-4">
      <div>
        <h3 className="text-base font-display font-semibold mb-1">3. Defaults + ejecutar</h3>
        <p className="text-sm text-base-content/65">
          Estos valores se aplican a todas las filas. Importarás <strong>{validRows.length}</strong>{" "}
          de {parsedRows.length} filas a <strong>{account?.name ?? "—"}</strong>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="cursor-pointer flex items-start gap-3 rounded-md border border-base-300/60 bg-base-200/30 p-3 min-w-0">
          <input
            type="checkbox"
            className="toggle toggle-sm toggle-primary shrink-0 mt-0.5"
            checked={affectsTotals}
            onChange={(e) => onAffectsTotalsChange(e.target.checked)}
          />
          <div className="text-left min-w-0 flex-1">
            <div className="text-sm font-medium">Afecta saldos y totales</div>
            <div className="text-xs text-base-content/55 leading-relaxed mt-0.5">
              Default OFF (histórico). Activa si el archivo es del periodo actual y aún no
              registrado.
            </div>
          </div>
        </label>
        <label className="cursor-pointer flex items-start gap-3 rounded-md border border-base-300/60 bg-base-200/30 p-3 min-w-0">
          <input
            type="checkbox"
            className="toggle toggle-sm toggle-primary shrink-0 mt-0.5"
            checked={isrApplicable}
            onChange={(e) => onIsrApplicableChange(e.target.checked)}
          />
          <div className="text-left min-w-0 flex-1">
            <div className="text-sm font-medium">Aplica para ISR / IVA</div>
            <div className="text-xs text-base-content/55 leading-relaxed mt-0.5">
              Default ON. Desactiva si el archivo contiene movimientos no fiscales.
            </div>
          </div>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="form-control block">
          <div className="label py-1">
            <span className="label-text text-sm font-medium">Categoría default ingresos</span>
          </div>
          <select
            className="select select-bordered select-sm w-full"
            value={defaultIngresoCat}
            onChange={(e) => onDefaultIngresoCatChange(e.target.value)}
          >
            <option value="">— Sin categoría —</option>
            {categories
              .filter((c) => c.kind === "ingreso")
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </label>
        <label className="form-control block">
          <div className="label py-1">
            <span className="label-text text-sm font-medium">Categoría default gastos</span>
          </div>
          <select
            className="select select-bordered select-sm w-full"
            value={defaultGastoCat}
            onChange={(e) => onDefaultGastoCatChange(e.target.value)}
          >
            <option value="">— Sin categoría —</option>
            {categories
              .filter((c) => c.kind === "gasto")
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </label>
        <label className="form-control block">
          <div className="label py-1">
            <span className="label-text text-sm font-medium">Status factura default</span>
          </div>
          <select
            className="select select-bordered select-sm w-full"
            value={defaultInvoiceStatus}
            onChange={(e) =>
              onDefaultInvoiceStatusChange(e.target.value as typeof defaultInvoiceStatus)
            }
          >
            <option value="pendiente">Pendiente de facturar</option>
            <option value="facturado">Con factura (CFDI)</option>
            <option value="deducible_sin_factura">Sin CFDI · deducible</option>
            <option value="no_aplica">No deducible</option>
          </select>
        </label>
      </div>

      {run.isError && (
        <div className="alert alert-error text-sm">
          Error: {run.error instanceof Error ? run.error.message : String(run.error)}
        </div>
      )}
      {run.isSuccess && (
        <div className="alert alert-success text-sm">
          Listo — se importaron <strong>{run.data.inserted}</strong> transacciones.
        </div>
      )}

      <div className="flex justify-between pt-3 border-t border-base-300/60">
        {run.isSuccess ? (
          <button type="button" onClick={onDone} className="btn btn-primary btn-sm ml-auto">
            Cerrar y empezar otra
          </button>
        ) : (
          <>
            <button type="button" onClick={onBack} className="btn btn-ghost btn-sm">
              <ArrowLeft className="size-4" /> Atrás
            </button>
            <button
              type="button"
              onClick={() => run.mutate()}
              disabled={run.isPending || validRows.length === 0}
              className="btn btn-primary btn-sm"
            >
              {run.isPending && <span className="loading loading-spinner loading-xs" />}
              Importar {validRows.length} filas
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Parsing helpers ──────────────────────────────────────────────────────

function parseRows(
  raw: Row[],
  mapping: ImportMapping,
  amountMode: "single" | "two_columns",
): ParsedRow[] {
  return raw.map((row) => {
    const dateRaw = mapping.date ? row[mapping.date] : "";
    const description = mapping.description ? (row[mapping.description] ?? "").trim() : "";
    const date = parseDate(dateRaw, mapping.dateFormat ?? "DD/MM/YYYY");

    let amountCents = 0;
    let kind: "ingreso" | "gasto" = "gasto";

    if (amountMode === "single") {
      const amt = parseAmount(mapping.amount ? row[mapping.amount] : "");
      if (amt === null) {
        return {
          date: date ?? "",
          description,
          kind,
          amountCents: 0,
          raw: row,
          error: "Monto inválido",
        };
      }
      kind = amt >= 0 ? "ingreso" : "gasto";
      amountCents = Math.abs(amt);
    } else {
      const cargo = parseAmount(mapping.cargoCol ? row[mapping.cargoCol] : "");
      const abono = parseAmount(mapping.abonoCol ? row[mapping.abonoCol] : "");
      if ((cargo ?? 0) > 0) {
        kind = "gasto";
        amountCents = Math.abs(cargo!);
      } else if ((abono ?? 0) > 0) {
        kind = "ingreso";
        amountCents = Math.abs(abono!);
      } else {
        return {
          date: date ?? "",
          description,
          kind,
          amountCents: 0,
          raw: row,
          error: "Sin monto",
        };
      }
    }

    if (!date) {
      return { date: "", description, kind, amountCents, raw: row, error: "Fecha inválida" };
    }
    if (amountCents === 0) {
      return { date, description, kind, amountCents, raw: row, error: "Monto cero" };
    }
    return { date, description, kind, amountCents, raw: row };
  });
}

/** Convierte "DD/MM/YYYY" o similar a "YYYY-MM-DD". */
function parseDate(input: string | undefined, format: string): string | null {
  if (!input) return null;
  const cleaned = input.toString().trim().replace(/\s.*/, ""); // por si trae hora
  const sep = format.includes("/") ? "/" : "-";
  const parts = cleaned.split(/[/-]/);
  if (parts.length !== 3) return null;
  const fmtParts = format.split(sep);
  const map: Record<string, string> = {};
  for (let i = 0; i < 3; i += 1) {
    map[fmtParts[i]!] = parts[i]!;
  }
  const y = map["YYYY"];
  const m = map["MM"];
  const d = map["DD"];
  if (!y || !m || !d) return null;
  if (!/^\d{4}$/.test(y) || !/^\d{1,2}$/.test(m) || !/^\d{1,2}$/.test(d)) return null;
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
}

/** Convierte "1,234.56" o "$1,234.56" o "(123.45)" a cents. */
function parseAmount(input: string | undefined): number | null {
  if (!input) return null;
  const s = String(input).trim();
  if (!s) return null;
  const isNegative = /^\(.*\)$/.test(s) || s.startsWith("-");
  const cleaned = s.replace(/[$,()\s-]/g, "").replace(/[^\d.]/g, "");
  if (!cleaned) return null;
  const n = parseFloat(cleaned);
  if (Number.isNaN(n)) return null;
  return Math.round(n * 100) * (isNegative ? -1 : 1);
}
